import type {
  EmulatorLaunchRequest,
  EmulatorRuntimeError,
  EmulatorRuntimeErrorCode,
  EmulatorRuntimeIdentity,
  EmulatorRuntimeFailureReason,
  EmulatorRuntimeListener,
  EmulatorRuntimeSnapshot,
  EmulatorRuntimeState,
} from './contracts';
import type { EmulatorRuntimeManifest } from './runtime-manifest';
import {
  assertOperationAllowed,
  assertRuntimeTransition,
  createRuntimeIdentity,
} from './runtime-state';

export class EmulatorRuntimeSession {
  readonly runtime: EmulatorRuntimeManifest;

  private current: EmulatorRuntimeSnapshot = {
    state: 'idle',
    identity: null,
    error: null,
    volume: 1,
    sequence: 0,
  };

  private readonly listeners = new Set<EmulatorRuntimeListener>();

  constructor(runtime: EmulatorRuntimeManifest) {
    this.runtime = runtime;
  }

  snapshot(): EmulatorRuntimeSnapshot {
    return this.current;
  }

  subscribe(listener: EmulatorRuntimeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  beginLaunch(request: EmulatorLaunchRequest): EmulatorRuntimeSnapshot {
    assertOperationAllowed('launch', this.current.state);
    const identity = createRuntimeIdentity(this.runtime, request);
    return this.transition('loading', identity, null);
  }

  markReady(): EmulatorRuntimeSnapshot {
    return this.transition('ready', this.requireIdentity(), null);
  }

  markPaused(): EmulatorRuntimeSnapshot {
    assertOperationAllowed('pause', this.current.state);
    return this.transition('paused', this.requireIdentity(), null);
  }

  markResumed(): EmulatorRuntimeSnapshot {
    assertOperationAllowed('resume', this.current.state);
    return this.transition('ready', this.requireIdentity(), null);
  }

  markRestarted(): EmulatorRuntimeSnapshot {
    assertOperationAllowed('restart', this.current.state);
    this.current = {
      ...this.current,
      error: null,
      sequence: this.current.sequence + 1,
    };
    this.emitState();
    return this.current;
  }

  setVolume(volume: number): EmulatorRuntimeSnapshot {
    assertOperationAllowed('set-volume', this.current.state);
    if (!Number.isFinite(volume) || volume < 0 || volume > 1) {
      throw new RangeError('Volume must be a finite number between 0 and 1.');
    }

    this.current = {
      ...this.current,
      volume,
      sequence: this.current.sequence + 1,
    };
    this.emitState();
    return this.current;
  }

  fail(
    code: Extract<EmulatorRuntimeErrorCode, 'RUNTIME_LOAD_FAILED' | 'RUNTIME_EXECUTION_FAILED'>,
    message: string,
    recoverable: boolean,
    reason?: EmulatorRuntimeFailureReason,
  ): EmulatorRuntimeSnapshot {
    const identity = this.current.identity;
    const error: EmulatorRuntimeError = {
      code,
      message,
      recoverable,
      context: {
        state: this.current.state,
        runtimeId: this.runtime.runtimeId,
        ...(identity ? { gameId: identity.gameId, variantId: identity.variantId } : {}),
        ...(reason ? { reason } : {}),
      },
    };
    const snapshot = this.transition('error', identity, error);

    for (const listener of this.listeners) {
      listener({ type: 'runtime-error', error, snapshot });
    }

    return snapshot;
  }

  terminate(): EmulatorRuntimeSnapshot {
    assertOperationAllowed('terminate', this.current.state);
    return this.transition('terminated', this.current.identity, null);
  }

  private transition(
    state: EmulatorRuntimeState,
    identity: EmulatorRuntimeIdentity | null,
    error: EmulatorRuntimeError | null,
  ): EmulatorRuntimeSnapshot {
    assertRuntimeTransition(this.current.state, state);
    this.current = {
      state,
      identity,
      error,
      volume: this.current.volume,
      sequence: this.current.sequence + 1,
    };
    this.emitState();
    return this.current;
  }

  private requireIdentity(): EmulatorRuntimeIdentity {
    if (!this.current.identity) {
      throw new Error('Runtime identity is unavailable before launch.');
    }
    return this.current.identity;
  }

  private emitState(): void {
    for (const listener of this.listeners) {
      listener({ type: 'state-changed', snapshot: this.current });
    }
  }
}
